import { useState, type MouseEvent } from 'react';
import { Bookmark, Heart, MessageCircle, MoreHorizontal, Repeat2, Share } from 'lucide-react';
import {
  GIST_REPORT_REASONS,
  type GistAction,
  type GistActionReply,
  type GistPostView,
  type GistReportReason,
} from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { ago } from '../lib/format';
import { shareLink } from '../lib/share';
import { StickerImage } from '../chat/message-list';
import { VoicePlayer } from '../chat/voice';
import { GistPhoto } from './camera';
import { GistAvatar, GistText, compact, type GistNav } from './parts';

// a post's share link: the game opens Gist on it
export const gistLink = (id: string) => `${window.location.origin}/?gist=${encodeURIComponent(id)}`;

// acting on a post: the change shown at once, the server's answer after (the post as it is now, or gone)
export function useGistAct(post: GistPostView, onChange: (next: GistPostView) => void, onGone: () => void) {
  const [busy, setBusy] = useState(false);
  const act = async (body: GistAction, optimistic?: GistPostView): Promise<GistActionReply | null> => {
    if (busy) return null;
    setBusy(true);
    if (optimistic) onChange(optimistic);
    try {
      const reply = await api.gist.act(post.id, body);
      if (reply.message) notify(reply.message);
      if (reply.post) onChange({ ...reply.post, registedBy: post.registedBy, feedAt: post.feedAt });
      else onGone();
      return reply;
    } catch (error) {
      if (optimistic) onChange(post);
      notify(describeError(error).message);
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { act, busy };
}

// One post, as Lagos Life's X-style Gist: the picture, name and time, its tag, the words (#tags and @names
// link to search) or a sticker, "Show this thread", then Reply, Re-gist, Like, Bookmark and Share. Tapping it
// opens the post; the menu deletes yours, or follows, unfollows or reports (and blocks) someone else.
export function GistPostCard({
  post,
  nav,
  onChange,
  onGone,
  line,
  feed = true,
}: {
  post: GistPostView;
  nav: GistNav;
  onChange: (next: GistPostView) => void;
  onGone: () => void;
  // in a thread: a line down to the next post
  line?: boolean;
  // in a feed (tap to open, "Show this thread"); false on the post's own page
  feed?: boolean;
}) {
  const { act, busy } = useGistAct(post, onChange, onGone);
  const name = post.news ? 'London Gist' : (post.author?.name ?? 'A player');
  const root = post.threadId ?? post.id;
  const openUser = () => post.author && nav.user(post.author.lifeId, post.author.name);
  return (
    <li
      className={`gist-post${post.news ? ' news' : ''}${post.held ? ' held' : ''}${feed ? ' tappable' : ''}`}
      onClick={feed ? () => nav.post(root) : undefined}
    >
      {post.registedBy && <p className="gist-regist">🔁 {post.registedBy} re-gisted</p>}
      <div className="gist-row">
        <span className="gist-side">
          <button
            type="button"
            className="gist-face"
            aria-label={post.author ? `${name}'s page` : name}
            disabled={!post.author}
            onClick={(event) => {
              event.stopPropagation();
              openUser();
            }}
          >
            <GistAvatar author={post.author} />
          </button>
          {line && <span className="gist-line" aria-hidden="true" />}
        </span>
        <div className="gist-main-col">
          <header className="gist-head">
            <strong className="gist-name">{name}</strong>
            {feed ? (
              <button
                type="button"
                className="gist-time"
                aria-label={`Open ${name}'s post`}
                onClick={(event) => {
                  event.stopPropagation();
                  nav.post(root);
                }}
              >
                · {ago(post.at)}
              </button>
            ) : (
              <small>· {ago(post.at)}</small>
            )}
            {post.parts > 1 && (
              <small className="gist-part">
                {post.part + 1}/{post.parts}
              </small>
            )}
            {!post.news && <GistMenu post={post} name={name} busy={busy} act={act} onChange={onChange} />}
          </header>
          {post.chip && (
            <span className="gist-chip">
              {post.chip.emoji} {post.chip.label}
            </span>
          )}
          {post.body && <GistText text={post.body} onLink={nav.search} />}
          {post.sticker && <StickerImage id={post.sticker} size={96} />}
          {post.photo && <GistPhoto photo={post.photo} />}
          {post.voice && (
            <span className="gist-voice" onClick={(event) => event.stopPropagation()}>
              <VoicePlayer playPath={post.voice.playPath} durationMs={post.voice.durationMs} />
            </span>
          )}
          {post.held && (
            <p className="gist-held">⏳ Held back after reports: only you can see it until the team looks.</p>
          )}
          <GistActions post={post} busy={busy} act={act} onReply={() => nav.post(root, { reply: true })} />
          {feed && post.parts > 1 && post.part === 0 && (
            <button
              type="button"
              className="gist-link gist-show-thread"
              onClick={(event) => {
                event.stopPropagation();
                nav.post(root);
              }}
            >
              Show this thread
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

// Reply, Re-gist, Like, Bookmark and Share, as Lagos
function GistActions({
  post,
  busy,
  act,
  onReply,
}: {
  post: GistPostView;
  busy: boolean;
  act: ReturnType<typeof useGistAct>['act'];
  onReply: () => void;
}) {
  const stop = (work: () => void) => (event: MouseEvent) => {
    event.stopPropagation();
    work();
  };
  const share = async () => {
    if ((await shareLink({ title: 'On London Gist', url: gistLink(post.threadId ?? post.id) })) === 'copied')
      notify('Link copied');
  };
  return (
    <footer className="gist-foot">
      <button type="button" aria-label="Reply" onClick={stop(onReply)}>
        <MessageCircle aria-hidden="true" /> {post.comments ? compact(post.comments) : ''}
      </button>
      {!post.news && (
        <button
          type="button"
          aria-pressed={post.registed}
          aria-label={post.registed ? 'Undo re-gist' : 'Re-gist'}
          className={post.registed ? 'on-regist' : ''}
          disabled={busy || post.mine}
          onClick={stop(
            () =>
              void act(
                { action: 'regist', on: !post.registed },
                { ...post, registed: !post.registed, regists: Math.max(0, post.regists + (post.registed ? -1 : 1)) },
              ),
          )}
        >
          <Repeat2 aria-hidden="true" /> {post.regists ? compact(post.regists) : ''}
        </button>
      )}
      <button
        type="button"
        aria-pressed={post.liked}
        aria-label={post.liked ? 'Unlike' : 'Like'}
        className={post.liked ? 'on-like' : ''}
        disabled={busy}
        onClick={stop(
          () =>
            void act(
              { action: 'like', on: !post.liked },
              { ...post, liked: !post.liked, likes: Math.max(0, post.likes + (post.liked ? -1 : 1)) },
            ),
        )}
      >
        <Heart aria-hidden="true" fill={post.liked ? 'currentColor' : 'none'} /> {post.likes ? compact(post.likes) : ''}
      </button>
      <button
        type="button"
        aria-pressed={post.bookmarked}
        aria-label={post.bookmarked ? 'Remove bookmark' : 'Bookmark'}
        className={post.bookmarked ? 'on-bookmark' : ''}
        disabled={busy}
        onClick={stop(
          () => void act({ action: 'bookmark', on: !post.bookmarked }, { ...post, bookmarked: !post.bookmarked }),
        )}
      >
        <Bookmark aria-hidden="true" fill={post.bookmarked ? 'currentColor' : 'none'} />
      </button>
      <button type="button" aria-label="Share" onClick={stop(() => void share())}>
        <Share aria-hidden="true" />
      </button>
    </footer>
  );
}

// the ⋯ menu: Delete (yours), or Follow / Unfollow and Report & block (someone else's)
function GistMenu({
  post,
  name,
  busy,
  act,
  onChange,
}: {
  post: GistPostView;
  name: string;
  busy: boolean;
  act: ReturnType<typeof useGistAct>['act'];
  onChange: (next: GistPostView) => void;
}) {
  const [menu, setMenu] = useState<'open' | 'report' | null>(null);
  const follow = async (on: boolean) => {
    setMenu(null);
    try {
      notify((await api.gist.follow(post.author!.lifeId, on)).message);
      onChange({ ...post, following: on });
    } catch (error) {
      notify(describeError(error).message);
    }
  };
  return (
    <span className="gist-menu-wrap" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className="gist-more"
        aria-label="More"
        aria-expanded={menu !== null}
        onClick={() => setMenu(menu ? null : 'open')}
      >
        <MoreHorizontal aria-hidden="true" />
      </button>
      {menu === 'open' && (
        <span className="gist-menu" role="group" aria-label="Post options">
          {post.mine ? (
            <button type="button" className="danger" disabled={busy} onClick={() => void act({ action: 'delete' })}>
              🗑 Delete
            </button>
          ) : (
            <>
              <button type="button" onClick={() => void follow(!post.following)}>
                {post.following ? `Unfollow ${name}` : `Follow ${name}`}
              </button>
              <button type="button" onClick={() => setMenu('report')}>
                ⚑ Report & block
              </button>
            </>
          )}
        </span>
      )}
      {menu === 'report' && (
        <span className="gist-menu" role="group" aria-label="What is wrong with this post">
          {(Object.keys(GIST_REPORT_REASONS) as GistReportReason[]).map((reason) => (
            <button key={reason} type="button" disabled={busy} onClick={() => void act({ action: 'report', reason })}>
              {GIST_REPORT_REASONS[reason]}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
